import SEO from "../components/SEO";
import PageHero from "../components/shared/PageHero";
import Eyebrow from "../components/shared/Eyebrow";
import TypedHeading from "../components/shared/TypedHeading";
import Reveal from "../components/shared/Reveal";
import CtaBanner from "../components/shared/CtaBanner";
import useI18n from "../i18n/useI18n";
import useCountUp from "../hooks/useCountUp";

const StatCounter = ({ target, suffix, label }) => {
  const { ref, value } = useCountUp(target);
  return (
    <div className="story-stat">
      <div className="story-stat__num" ref={ref}>
        {value}
        {suffix}
      </div>
      <div className="story-stat__label">{label}</div>
    </div>
  );
};

const StoryPage = () => {
  const { t, lang } = useI18n();
  const wk = lang === "he" ? " שב'" : " wks";
  const statMeta = [
    { target: 12, suffix: "+" },
    { target: 4, suffix: wk },
    { target: 100, suffix: "%" },
    { target: 2, suffix: wk },
  ];
  const statLabels = t("story.stats");
  const timeline = t("story.timeline");
  const principles = t("story.principles");

  return (
    <div className="story-page">
      <SEO title={`CodeIL · ${t("nav.story")}`} description={t("story.lede")} path="/story" />

      <PageHero eyebrow={t("story.eyebrow")} titleWords={t("story.title")} lede={t("story.lede")}>
        <div className="story-stats">
          {statMeta.map((s, i) => (
            <StatCounter key={i} target={s.target} suffix={s.suffix} label={statLabels[i]} />
          ))}
        </div>
      </PageHero>

      {/* Who we are */}
      <section className="section section--alt story-who">
        <div className="story-who__inner">
          <div>
            <Reveal><Eyebrow>{t("story.whoEyebrow")}</Eyebrow></Reveal>
            <TypedHeading text={t("story.whoTitle")} className="story-who__title" />
            <Reveal as="p" className="story-who__p">{t("story.whoP1")}</Reveal>
            <Reveal as="p" className="story-who__p">{t("story.whoP2")}</Reveal>
          </div>
          <Reveal className="media-frame media-frame--43" aria-hidden="true">
            <span className="media-frame__tag">TEAM</span>
          </Reveal>
        </div>
      </section>

      {/* Timeline */}
      <section className="section story-timeline">
        <div className="story-timeline__inner">
          <Reveal><Eyebrow>{t("story.timelineEyebrow")}</Eyebrow></Reveal>
          <TypedHeading text={t("story.timelineTitle")} className="story-timeline__title" />
          <div className="story-timeline__track">
            <div className="story-timeline__rail" aria-hidden="true" />
            <div className="story-timeline__items">
              {Array.isArray(timeline) &&
                timeline.map((item, i) => (
                  <Reveal as="article" className="story-timeline__item" key={i}>
                    <span className="story-timeline__dot" aria-hidden="true" />
                    <div className="story-timeline__tag">{item.tag}</div>
                    <h3 className="story-timeline__item-title">{item.title}</h3>
                    <p className="story-timeline__desc">{item.desc}</p>
                  </Reveal>
                ))}
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section section--alt story-principles">
        <div className="story-principles__inner">
          <Reveal><Eyebrow>{t("story.principlesEyebrow")}</Eyebrow></Reveal>
          <TypedHeading text={t("story.principlesTitle")} className="story-principles__title" />
          <div className="story-principles__grid">
            {Array.isArray(principles) &&
              principles.map((p, i) => (
                <Reveal
                  as="article"
                  key={i}
                  className={`principle-card${i === principles.length - 1 ? " principle-card--wide" : ""}`}
                >
                  <span className="principle-card__num">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="principle-card__title">{p.title}</h3>
                  <p className="principle-card__desc">{p.desc}</p>
                </Reveal>
              ))}
          </div>
        </div>
      </section>

      <CtaBanner
        title={t("story.ctaTitle")}
        lede={t("story.ctaLede")}
        primary={{ label: t("story.ctaPrimary"), kind: "anchor", target: "ai" }}
        secondary={{ label: t("story.ctaSecondary"), kind: "route", target: "/services" }}
      />
    </div>
  );
};

export default StoryPage;
